import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowRight, ExternalLink, ShieldCheck } from "lucide-react";
import Nav from "@/components/marketing/Nav";
import Footer from "@/components/marketing/Footer";
import { Reveal } from "@/components/Reveal";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";

export default function DestinationDetail() {
  const { key } = useParams();
  const { user } = useAuth();
  const [corridor, setCorridor] = useState(null);
  const [notFound, setNotFound] = useState(false);
  useEffect(() => {
    api.get(`/corridors/${key}`).then((r) => setCorridor(r.data)).catch(() => setNotFound(true));
  }, [key]);

  if (notFound) return (
    <div className="min-h-screen bg-softcream"><Nav />
      <div className="mx-auto max-w-page px-6 py-24 text-center">
        <h1 className="font-serif text-3xl text-heading">Destination not found</h1>
        <Link to="/destinations" className="mt-4 inline-block text-tblue hover:underline">Back to destinations</Link>
      </div><Footer /></div>
  );
  if (!corridor) return <div className="min-h-screen bg-softcream"><Nav /><div className="p-24 text-center text-muted">Loading…</div></div>;

  const startTo = user ? "/app/new" : "/register";
  const reqSet = corridor.requirement_sets?.[0];

  return (
    <div className="min-h-screen bg-softcream">
      <Nav />
      <div className="relative h-64 overflow-hidden sm:h-72 md:h-96">
        <img src={corridor.image_url} alt={corridor.destination} className="h-full w-full object-cover" />
        <div className="absolute inset-0 bg-deepnavy/45" />
        <div className="absolute bottom-0 mx-auto w-full max-w-page px-5 pb-8 sm:px-8 lg:px-12">
          <Reveal>
            <h1 className="font-serif text-3xl text-white sm:text-4xl md:text-5xl">{corridor.destination}</h1>
            <p className="mt-2 max-w-2xl text-white/80">{corridor.summary}</p>
          </Reveal>
        </div>
      </div>
      <section className="mx-auto grid max-w-page gap-10 px-5 py-14 sm:px-8 lg:grid-cols-3 lg:px-12">
        <div className="lg:col-span-2">
          <h2 className="font-serif text-2xl text-heading">Visa types</h2>
          <div className="mt-4 space-y-3">
            {corridor.visa_categories.map((v, i) => (
              <Reveal key={v.key} delay={i * 0.06} className="flex flex-wrap items-center justify-between gap-3 rounded-md border border-hborder bg-white p-5 transition- duration-300 " data-testid={`visatype-${v.key}`}>
                <div>
                  <p className="font-semibold text-heading">{v.name}</p>
                  <p className="mt-0.5 text-sm text-muted">{v.available ? "Open for preparation" : "Not yet available"}</p>
                </div>
                {v.available && corridor.availability === "Available" ? (
                  <Link to={startTo} className="inline-flex items-center gap-1.5 rounded bg-brandyellow px-5 py-2 text-sm font-semibold text-heading transition-transform " data-testid={`start-${v.key}`}>
                    Prepare <ArrowRight size={15} />
                  </Link>
                ) : (
                  <span className="rounded border border-hborder px-4 py-2 text-xs font-medium text-muted">Coming soon</span>
                )}
              </Reveal>
            ))}
          </div>

          {reqSet && (
            <div className="mt-10">
              <h2 className="font-serif text-2xl text-heading">What you will need</h2>
              <p className="mt-1 text-sm text-muted">
                Requirement set v{reqSet.version} · last verified {reqSet.last_verified_date}
              </p>
              <div className="mt-4 space-y-2">
                {reqSet.requirements.map((r, i) => (
                  <Reveal key={r.code} delay={Math.min(i * 0.04, 0.3)} className="rounded-lg border border-hborder bg-white p-4 transition- duration-300 ">
                    <div className="flex items-center justify-between gap-3">
                      <p className="font-medium text-heading">{r.title}</p>
                      <span className={`shrink-0 rounded px-2 py-0.5 text-xs ${r.required ? "bg-cream text-coral" : "bg-gray-100 text-muted"}`}>
                        {r.required ? "Required" : "If applicable"}
                      </span>
                    </div>
                    <p className="mt-1 text-sm text-muted">{r.description}</p>
                    {r.source?.url && (
                      <a href={r.source.url} target="_blank" rel="noreferrer" className="mt-2 inline-flex items-center gap-1 text-xs text-tblue hover:underline">
                        {r.source.name} <ExternalLink size={12} />
                      </a>
                    )}
                  </Reveal>
                ))}
              </div>
            </div>
          )}
        </div>
        <aside className="h-max rounded-md border border-hborder bg-white p-6 lg:sticky lg:top-24">
          <ShieldCheck className="text-tblue" size={24} />
          <h3 className="mt-3 font-semibold text-heading">Prepare with Holavisto</h3>
          <p className="mt-2 text-sm text-muted">We organize your requirements, check your documents for consistency and help you reach a submission-ready state. We do not decide your visa.</p>
          <Link to={startTo} className="mt-5 flex items-center justify-center gap-2 rounded bg-navy px-6 py-3 text-sm font-semibold text-white transition-transform " data-testid="detail-start-btn">
            {corridor.availability === "Available" ? "Start your application" : "Get notified"} <ArrowRight size={15} />
          </Link>
        </aside>
      </section>
      <Footer />
    </div>
  );
}
